import React, { use, ReactNode, useEffect, useRef, useState, createContext } from 'react'
import constate from 'constate'
import { omit } from 'lodash'
import { HTMLChakraProps } from '~/design_system'
import { TOP_NAV_HEIGHT } from '~/common/scrollUtils'
import { $thm } from '~/common/themeSx'
import { Box, Stack, StackProps, forwardRef } from '../../chakra'

type JumpToListItemWithRef = {
  id: string
  name: ReactNode
  el: HTMLElement
  category?: string
}

/**
 * Context for JumpToCategory
 */
const JumpToCategoryContext = createContext<string | undefined>(undefined)

/**
 * Hook to read the current category from context
 */
export const useJumpToCategory = () => use(JumpToCategoryContext)

/**
 * JumpToCategory
 *
 * A wrapper component that provides category context to all JumpToSectionHeader
 * and JumpToAnchor components within it. This eliminates the need to pass the
 * category prop to each individual section.
 *
 * Usage:
 * ```tsx
 * <JumpToCategory category="General Information">
 *   <Section>
 *     <JumpToSectionHeader anchorId="name" sectionTitle="Name" />
 *     <SectionContent>...</SectionContent>
 *   </Section>
 *   <Section>
 *     <JumpToSectionHeader anchorId="address" sectionTitle="Address" />
 *     <SectionContent>...</SectionContent>
 *   </Section>
 * </JumpToCategory>
 * ```
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/patterns-jump-to-menu--docs
 */
export function JumpToCategory({ category, children }: { category: string; children: ReactNode }) {
  return <JumpToCategoryContext value={category}>{children}</JumpToCategoryContext>
}

/**
 * JumpToAnchor
 *
 * Put this anchor anywhere you need a jump to menu item that sends you here
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/patterns-jump-to-menu--docs
 */
export function JumpToAnchor({
  anchorId,
  name,
  category: categoryProp,
  children
}: HTMLChakraProps<'span'> & { name?: string; anchorId: string; category?: string }) {
  const { registerJumpToItem, unregisterJumpToItem } = useJumpToMenuContext()
  const categoryFromContext = useJumpToCategory()
  const category = categoryProp ?? categoryFromContext

  useEffect(
    () => () => {
      if (unregisterJumpToItem) {
        unregisterJumpToItem(anchorId)
      }
    },
    []
  )

  const scrollOffset = category ? 48 : 32
  return (
    <Box
      as="span"
      id={anchorId}
      ref={(el: HTMLElement | null) => {
        if (anchorId && el && registerJumpToItem) {
          registerJumpToItem(anchorId, name ?? children, el, category)
        }
      }}
      // Offset for floating menu at top
      sx={{ scrollMarginTop: `${TOP_NAV_HEIGHT + scrollOffset}px` }}>
      {children}
    </Box>
  )
}

/**
 * JumpToMenu
 *
 * A list of form sections to jump to each section more easily by clicking on the section on
 * the left hand side link. This component is good to use for very long forms.
 *
 * This is the parent wrapper component which accepts StackProps.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/patterns-jump-to-menu--docs
 */
export const JumpToMenu = forwardRef(({ children, sx, ...props }: StackProps, ref) => (
  <JumpToMenuContextProvider>
    <Stack
      data-jump-to-menu
      direction="row"
      borderRadius="inherit"
      ref={ref}
      sx={$thm({
        gap: { $canvas: 0 },
        ...sx
      })}
      {...props}>
      {children}
    </Stack>
  </JumpToMenuContextProvider>
))

/**
 * JumpToContent
 *
 * This component is a child component of JumpToMenu which represents the content area of the jump to menu. This type of layout
 * is ideal for long forms where a easy way to scroll through the content is required. The scroll position is determined by
 * the headingRefs passed into the SidebarList.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/patterns-jump-to-menu--docs
 */
export const JumpToContent = forwardRef(({ children, ...props }: StackProps, ref) => {
  return (
    <Stack
      p={6}
      spacing={4}
      ref={ref}
      flex="1"
      sx={$thm({
        '[data-card] &': {
          pt: { $canvas: 0 },
          pl: { $canvas: 6 },
          pr: { $canvas: 0 }
        }
      })}
      {...props}>
      {children}
    </Stack>
  )
})

/**
 * Context for JumpToMenu that allows anchors/sections to self-register to the menu
 *
 * Items should automatically sort in the list based on their DOM ordering
 */
export const [JumpToMenuContextProvider, useJumpToMenuContext] = constate(() => {
  const [targets, setTargets] = useState<Record<string, JumpToListItemWithRef>>({})
  const lastLoggedErrorRef = useRef<string | null>(null)

  const registerJumpToItem = (id: string, name: ReactNode, el: HTMLElement, category?: string) => {
    const item = {
      id,
      name,
      el,
      category
    }

    setTargets(m => {
      if (m[item.id]) {
        return m
      }
      return { ...m, [id]: item }
    })
  }

  const unregisterJumpToItem = (id: string) => {
    setTargets(m => omit(m, [id]))
  }

  // Sort target list by DOM order
  const sortedTargets = Object.values(targets).sort((a, b) => {
    // eslint-disable-next-line no-bitwise
    return a.el.compareDocumentPosition(b.el) & 2 ? 1 : -1
  })

  // Compute validation result
  const validation = validateJumpToItems(sortedTargets)

  // Log errors (only once per unique error)
  logValidationError(validation, lastLoggedErrorRef)

  return {
    targets: sortedTargets,
    registerJumpToItem,
    unregisterJumpToItem,
    validation
  }
})

type ValidationResult = {
  isValid: boolean
  errorType?: 'mixed'
  errorMessage?: string
  uncategorizedItems?: Array<{ id: string; name: ReactNode }>
}

/**
 * Validates jump-to menu items for proper category usage
 */
function validateJumpToItems(sortedTargets: JumpToListItemWithRef[]): ValidationResult {
  if (sortedTargets.length === 0) {
    return { isValid: true }
  }

  const hasCategorized = sortedTargets.some(item => item.category != null)
  const hasUncategorized = sortedTargets.some(item => item.category == null)

  // Check for mixed usage
  if (hasCategorized && hasUncategorized) {
    const uncategorizedItems = sortedTargets
      .filter(item => !item.category)
      .map(item => ({ id: item.id, name: item.name }))

    return {
      isValid: false,
      errorType: 'mixed',
      errorMessage: `Mixed usage detected: both categorized and uncategorized items found.`,
      uncategorizedItems
    }
  }

  return { isValid: true }
}

/**
 * Logs validation errors to console (only once per unique error).
 * Accepts a ref to scope error dedup per JumpToMenu instance.
 */
function logValidationError(validation: ValidationResult, lastLoggedError: React.MutableRefObject<string | null>) {
  if (!validation.isValid && validation.errorMessage) {
    const errorKey = `${validation.errorType}-${validation.errorMessage}`
    if (lastLoggedError.current !== errorKey) {
      // eslint-disable-next-line no-param-reassign
      lastLoggedError.current = errorKey
      // eslint-disable-next-line no-console
      console.error(`[JumpToMenu] ${validation.errorMessage}`)
      if (validation.uncategorizedItems && validation.uncategorizedItems.length > 0) {
        // eslint-disable-next-line no-console
        console.error(
          `[JumpToMenu] Uncategorized items:`,
          validation.uncategorizedItems.map(item => ({
            id: item.id,
            name: typeof item.name === 'string' ? item.name : item.id
          }))
        )
      }
    }
  } else if (validation.isValid) {
    // Reset error tracking when validation passes
    // eslint-disable-next-line no-param-reassign
    lastLoggedError.current = null
  }
}
